import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Loader2, 
  Copy, 
  Download, 
  RefreshCw, 
  Sparkles,
  CheckCircle,
  Heart,
  Briefcase,
  Scale,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PricingModal from '../components/PricingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CURRENCIES = [
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'AED', label: 'AED (د.إ)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'AUD', label: 'AUD ($)' },
  { value: 'JPY', label: 'JPY (¥)' },
  { value: 'INR', label: 'INR (₹)' },
  { value: 'CHF', label: 'CHF (Fr)' },
  { value: 'ZAR', label: 'ZAR (R)' },
  { value: 'NGN', label: 'NGN (₦)' },
  { value: 'SEK', label: 'SEK (kr)' },
  { value: 'NOK', label: 'NOK (kr)' },
  { value: 'DKK', label: 'DKK (kr)' },
  { value: 'SGD', label: 'SGD ($)' },
  { value: 'HKD', label: 'HKD ($)' },
  { value: 'NZD', label: 'NZD ($)' },
  { value: 'BRL', label: 'BRL (R$)' },
  { value: 'MXN', label: 'MXN ($)' },
  { value: 'PLN', label: 'PLN (zł)' },
  { value: 'TRY', label: 'TRY (₺)' },
  { value: 'KES', label: 'KES (KSh)' },
  { value: 'GHS', label: 'GHS (₵)' },
  { value: 'SAR', label: 'SAR (﷼)' },
  { value: 'QAR', label: 'QAR (﷼)' },
  { value: 'KWD', label: 'KWD (د.ك)' },
  { value: 'BHD', label: 'BHD (BD)' },
  { value: 'OMR', label: 'OMR (﷼)' },
  { value: 'JMD', label: 'JMD ($)' },
  { value: 'TTD', label: 'TTD ($)' },
];

const PAYMENT_TERMS = [
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '45', label: '45 days' },
  { value: '60', label: '60 days' },
  { value: 'other', label: 'Other (specify)' },
];

const COUNTRIES = [
  { value: 'UK', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'IN', label: 'India' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'AE', label: 'UAE' },
  { value: 'OTHER', label: 'Other' },
];

const STORAGE_KEY = 'sif_email_form';

export default function EmailGeneratorPage() {
  const { planInfo, fetchPlanInfo, user } = useAuth();
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      business_name: '',
      client_name: '',
      amount_owed: '',
      currency: 'GBP',
      invoice_date: '',
      payment_terms: '30',
      custom_payment_terms: '',
      country: 'UK',
      notes: '',
    };
  });

  const [letters, setLetters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('friendly');
  const [copied, setCopied] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [pricingTrigger, setPricingTrigger] = useState(null);

  const plan = planInfo?.plan || 'free';
  const credits = planInfo?.credits || 0;
  const emailCost = planInfo?.credit_costs?.email_generate || 3;
  const downloadCost = planInfo?.credit_costs?.download || 1;

  // Auto-save form state to localStorage
  React.useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }, 500);
    return () => clearTimeout(timer);
  }, [formData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    // Validation
    if (!formData.business_name || !formData.client_name || !formData.amount_owed || !formData.invoice_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Handle custom payment terms
    let paymentTerms = formData.payment_terms === 'other' 
      ? parseInt(formData.custom_payment_terms) || 30
      : parseInt(formData.payment_terms);

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/letters/generate`, {
        ...formData,
        amount_owed: parseFloat(formData.amount_owed),
        payment_terms: paymentTerms,
      }, { withCredentials: true });

      setLetters(response.data);
      toast.success('Emails generated successfully!');
      localStorage.removeItem(STORAGE_KEY);
      fetchPlanInfo();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.startsWith('upgrade_required')) {
        setPricingTrigger('no_credits');
        setShowPricing(true);
      } else {
        toast.error(detail || 'Failed to generate emails');
      }
    }
    setLoading(false);
  };

  const handleCopy = async () => {
    if (!letters) return;
    
    const content = letters[activeTab];
    await navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = async () => {
    if (!letters) return;

    if (credits < downloadCost) {
      setPricingTrigger('no_credits');
      setShowPricing(true);
      return;
    }

    try {
      const emailTypes = {
        friendly: 'Friendly Reminder',
        firm: 'Firm Follow-Up',
        legal: 'Final Demand Notice'
      };

      const response = await axios.post(
        `${API_URL}/api/letters/pdf`,
        {
          content: letters[activeTab],
          letter_type: emailTypes[activeTab],
          business_name: formData.business_name
        },
        { 
          responseType: 'blob',
          withCredentials: true 
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${activeTab}_email.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
      fetchPlanInfo();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.startsWith('upgrade_required')) {
        setPricingTrigger('no_credits');
        setShowPricing(true);
      } else {
        toast.error('Failed to download PDF');
      }
    }
  };

  const toneIcons = {
    friendly: Heart,
    firm: Briefcase,
    legal: Scale
  };

  return (
    <div className="min-h-screen bg-[#050507] pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">Professional</span>
          <h1 className="font-clash text-4xl sm:text-5xl font-medium text-white mt-2 mb-4" data-testid="email-generator-title">
            Payment Email Generator
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Generate professional collection emails instantly. Choose from friendly reminders to final demand notices.
          </p>
          <div className="mt-8 max-w-2xl mx-auto overflow-hidden rounded-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/dknwwvge_pexels-polina-zimmerman-3747075.jpg" 
              alt="Person typing on laptop" 
              className="w-full h-48 sm:h-64 object-cover opacity-60"
              data-testid="email-generator-hero-image"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <div className="bg-surface p-8 rounded-md border border-white/8 card-glow">
            <h2 className="font-clash text-xl font-medium text-white mb-6">Invoice Details</h2>
            
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Your Name / Business Name *</Label>
                  <Input
                    placeholder="Name"
                    value={formData.business_name}
                    onChange={(e) => handleInputChange('business_name', e.target.value)}
                    className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    data-testid="input-business-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Client Name *</Label>
                  <Input
                    placeholder="ABC Events Ltd"
                    value={formData.client_name}
                    onChange={(e) => handleInputChange('client_name', e.target.value)}
                    className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    data-testid="input-client-name"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Amount Owed *</Label>
                  <Input
                    type="number"
                    placeholder="1500.00"
                    value={formData.amount_owed}
                    onChange={(e) => handleInputChange('amount_owed', e.target.value)}
                    className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    data-testid="input-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Currency</Label>
                  <Select value={formData.currency} onValueChange={(v) => handleInputChange('currency', v)}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="select-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121216] border-white/10 max-h-60 overflow-y-auto">
                      {CURRENCIES.map(c => (
                        <SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/5">
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Invoice Date *</Label>
                  <Input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                    className="bg-black/50 border-white/10 text-white"
                    data-testid="input-invoice-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70">Payment Terms</Label>
                  <Select value={formData.payment_terms} onValueChange={(v) => handleInputChange('payment_terms', v)}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="select-terms">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121216] border-white/10">
                      {PAYMENT_TERMS.map(t => (
                        <SelectItem key={t.value} value={t.value} className="text-white hover:bg-white/5">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Custom Payment Terms Input */}
              {formData.payment_terms === 'other' && (
                <div className="space-y-2">
                  <Label className="text-white/70">Specify Payment Terms (days) *</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 21"
                    value={formData.custom_payment_terms}
                    onChange={(e) => handleInputChange('custom_payment_terms', e.target.value)}
                    className="bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    data-testid="input-custom-terms"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-white/70">Country (for tone localization)</Label>
                <Select value={formData.country} onValueChange={(v) => handleInputChange('country', v)}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="select-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121216] border-white/10">
                    {COUNTRIES.map(c => (
                      <SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/5">
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Additional Notes (optional)</Label>
                <Textarea
                  placeholder="Any additional context about the agreement..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="bg-black/50 border-white/10 text-white placeholder:text-white/30 min-h-[100px]"
                  data-testid="input-notes"
                />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full bg-[#FF4500] hover:bg-[#CC3700] text-white py-6 text-lg btn-active"
                data-testid="generate-emails-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Emails...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate All Emails ({emailCost} credits)
                  </>
                )}
              </Button>
              <p className="text-center text-white/30 text-xs mt-2">You have {credits} credits remaining</p>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-surface p-8 rounded-md border border-white/8 card-glow">
            <h2 className="font-clash text-xl font-medium text-white mb-6">Generated Emails</h2>

            {!letters ? (
              <div className="flex flex-col items-center justify-center h-[400px] text-center">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-white/30" />
                </div>
                <p className="text-white/50 mb-2">No emails generated yet</p>
                <p className="text-white/30 text-sm">Fill in the form and click generate</p>
              </div>
            ) : (
              <>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="w-full grid grid-cols-3 bg-black/30 p-1 mb-6">
                    {['friendly', 'firm', 'legal'].map((tone) => {
                      const Icon = toneIcons[tone];
                      return (
                        <TabsTrigger
                          key={tone}
                          value={tone}
                          className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-white/60 capitalize"
                          data-testid={`tab-${tone}`}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {tone === 'legal' ? 'Final' : tone}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  {['friendly', 'firm', 'legal'].map((tone) => (
                    <TabsContent key={tone} value={tone} className="mt-0">
                      <div className="bg-black/30 rounded-md p-4 max-h-[350px] overflow-y-auto">
                        <pre className="whitespace-pre-wrap text-white/80 text-sm font-sans leading-relaxed" data-testid={`letter-content-${tone}`}>
                          {letters[tone]}
                        </pre>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>

                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={handleCopy}
                    variant="outline"
                    className="flex-1 border-white/10 text-white hover:bg-white/5"
                    data-testid="copy-email-btn"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleDownloadPDF}
                    variant="outline"
                    className="flex-1 border-white/10 text-white hover:bg-white/5"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button
                    onClick={handleGenerate}
                    variant="outline"
                    className="border-white/10 text-white hover:bg-white/5"
                    disabled={loading}
                    data-testid="regenerate-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 flex items-start gap-3 p-4 rounded-md bg-yellow-500/10 border border-yellow-500/20">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <p className="text-yellow-500/80 text-sm">
            <strong>Disclaimer:</strong> This platform does not provide legal advice. The emails generated are for general guidance only. 
            For legal matters, please consult a qualified professional.
          </p>
        </div>
      </div>

      {/* Pricing Modal */}
      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
        trigger={pricingTrigger}
      />
    </div>
  );
}
